/**
 * Exercice « sur la feuille de brouillon » — le décor et le clavier.
 *
 *  - `PaperPage`       : fond intégral (petits carreaux + marge rouge) ;
 *  - `PaperChoiceList` : les propositions écrites sur la feuille quand elles ne
 *                        tiennent pas dans une touche (mode « lettres ») ;
 *  - `AnswerKeys`      : les touches de réponse + « Check » ;
 *  - `AnswerDock`      : le dock fixe du bas (pose `--dock-h`) ;
 *  - `PaperNextButton` : « Suivant » fixé en bas à droite, une fois la réponse donnée.
 */

'use client';

import './paper.css';
import { useEffect, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { Check, CircleHelp, Loader2, PencilLine } from 'lucide-react';
import type { ExerciseVisualBlock } from '@jeraisonne/ai/visual-blocks';
import { cn } from '../../lib/utils';
import { MarkdownMath } from '../content/markdown-math';
import { VisualBlocksRenderer } from '../content/visual-blocks/visual-blocks-renderer';
import { keyboardChoiceIndex, keyLetter, visibleLength, type KeyLayout } from './choice-keys';

/** Réponse assez courte pour 4 touches sur une rangée (téléphone 375 px). */
export const TINY_KEY_MAX = 3;

/** Une proposition du QCM. */
export interface PaperQcmOption {
  id: string;
  label: string;
  visuals: ExerciseVisualBlock[];
  is_correct: boolean;
}

/**
 * État d'un QCM joué au clavier du dock : l'app le calcule (`usePaperQcm`),
 * le showcase du site le rejoue (`toggle` / `check` sans effet).
 */
export interface PaperQcm {
  options: PaperQcmOption[];
  multiple: boolean;
  layout: KeyLayout;
  /** choix avant « Check » */
  selected: string[];
  /** réponse envoyée : choix coché(s) */
  chosen: string[];
  revealed: boolean;
  /** les touches sont visibles (délai de lecture écoulé) */
  shown: boolean;
  /** envoi en cours */
  busy: boolean;
  toggle: (id: string) => void;
  check: () => void;
}

export function PaperPage({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('pp-root pp-page min-h-dvh', className)} data-testid="paper-page">
      {children}
    </div>
  );
}

/** La colonne écrite (marge rouge à gauche). */
export function PaperColumn({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('pp-column mx-auto max-w-3xl', className)}>{children}</div>;
}

type KeyState = 'idle' | 'selected' | 'good' | 'bad' | 'off';

function keyState(qcm: PaperQcm, id: string, isCorrect: boolean): KeyState {
  if (!qcm.revealed) return qcm.selected.includes(id) ? 'selected' : 'idle';
  if (isCorrect) return 'good';
  if (qcm.chosen.includes(id)) return 'bad';
  return 'off';
}

const KEY_STATE_LABEL: Record<KeyState, string> = {
  idle: '',
  selected: ', choisie',
  good: ', bonne réponse',
  bad: ', ta réponse, fausse',
  off: '',
};

/** Propositions écrites sur la feuille (mode « lettres »). Toucher = choisir. */
export function PaperChoiceList({ qcm }: { qcm: PaperQcm }) {
  if (qcm.layout !== 'letters') return null;
  return (
    <ol
      className={cn(
        'mt-3 space-y-2 transition-opacity duration-700',
        qcm.shown ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      aria-label="Propositions"
      aria-hidden={!qcm.shown || undefined}
    >
      {qcm.options.map((o, i) => {
        const st = keyState(qcm, o.id, o.is_correct);
        return (
          <li key={o.id}>
            <button
              type="button"
              tabIndex={-1}
              className={cn(
                'pp-written flex w-full items-start gap-2.5 rounded-xl py-1 text-left',
                st === 'off' && 'opacity-50',
              )}
              data-state={st}
              disabled={qcm.revealed || qcm.busy}
              onClick={() => qcm.toggle(o.id)}
            >
              <span className="pp-written-letter mt-0.5">{keyLetter(i)}</span>
              <span className="min-w-0 flex-1 text-[15px]">
                <MarkdownMath>{o.label}</MarkdownMath>
                {o.visuals.length > 0 && (
                  <span className="pp-figure mt-1 block">
                    <VisualBlocksRenderer blocks={o.visuals} />
                  </span>
                )}
              </span>
              {st === 'good' && <span className="font-extrabold text-[var(--sc-good)]">✓</span>}
              {st === 'bad' && <span className="font-extrabold text-[var(--sc-bad)]">✗</span>}
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function AnswerKeys({
  qcm,
  found,
  crossed,
  error,
  onWrite,
  onHelp,
  onNext,
  nextLabel = 'Suivant',
  keyboard = true,
}: {
  qcm: PaperQcm;
  /** réponse envoyée : bouton pour passer à la suite (à la place de « Check ») */
  onNext?: () => void;
  nextLabel?: string;
  /** raccourcis clavier (faux pour un rejeu automatique) */
  keyboard?: boolean;
  /** « ? » : relance la prise en main */
  onHelp?: () => void;
  /** choix que le brouillon établit */
  found?: string[];
  /** choix que le brouillon montre faux */
  crossed?: string[];
  error?: string | null;
  /** « Écrire » : la palette du brouillon monte */
  onWrite?: () => void;
}) {
  const n = qcm.options.length;
  const letters = qcm.layout === 'letters';

  // clavier physique : 1-9 / A-Z choisissent, Entrée envoie
  const qcmRef = useRef(qcm);
  qcmRef.current = qcm;
  useEffect(() => {
    if (!keyboard) return;
    const onKey = (e: KeyboardEvent) => {
      const q = qcmRef.current;
      if (q.revealed || !q.shown || e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest?.('input, textarea, [contenteditable="true"], [role="dialog"]')) return;
      if (e.key === 'Enter') {
        if (q.selected.length) {
          e.preventDefault();
          q.check();
        }
        return;
      }
      const i = keyboardChoiceIndex(e.key, q.options.length);
      if (i !== null) q.toggle(q.options[i]!.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [keyboard]);

  // réponses très courtes (« 14 », « −2 ») : toutes sur une ligne, même sur
  // téléphone — le dock reste bas et la feuille garde la place. Au-delà de
  // 3 caractères (« x = 9 »), une touche de ~80 px coupe le texte.
  const tiny = qcm.options.every(
    (o) => o.visuals.length === 0 && visibleLength(o.label) <= TINY_KEY_MAX,
  );
  const cols = letters ? Math.min(n, 6) : tiny ? (n <= 4 ? n : 3) : n <= 2 ? n : 2;
  const colsSm = letters ? Math.min(n, 6) : Math.min(n, 4);

  return (
    <div
      className={cn(
        'space-y-2 transition-opacity duration-700',
        qcm.shown ? 'opacity-100' : 'opacity-40',
      )}
    >
      <div className="flex items-center gap-2">
        <p className="font-display text-[11px] font-semibold uppercase tracking-wider text-[var(--sc-muted)]">
          {qcm.revealed
            ? 'Ta réponse'
            : qcm.shown
              ? qcm.multiple
                ? 'Plusieurs réponses possibles'
                : 'Réponse unique'
              : 'Lis bien la question…'}
        </p>
        <span className="flex-1" />
        {onHelp && !qcm.revealed && (
          <button
            type="button"
            className="-my-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-[var(--sc-muted)]"
            aria-label="Comment répondre ?"
            onClick={onHelp}
          >
            <CircleHelp className="h-[18px] w-[18px]" aria-hidden />
          </button>
        )}
        {onWrite && !qcm.revealed && (
          <button
            type="button"
            className="font-display -my-2 inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 text-xs font-bold text-[var(--sc-gold-ink)]"
            onClick={onWrite}
            data-testid="dock-write"
            data-tuto="paper-write"
          >
            <PencilLine className="h-4 w-4" aria-hidden />
            Écrire au brouillon
          </button>
        )}
      </div>

      {/* téléphone : Check sous les touches (sous le pouce) ; tablette et ordinateur : sur la même rangée */}
      <div
        className="flex flex-col gap-2 sm:flex-row sm:items-stretch"
        data-testid="dock-answer-row"
      >
        <div
          role={qcm.multiple ? 'group' : 'radiogroup'}
          aria-label="Propositions"
          data-tuto="paper-keys"
          className="grid gap-2 [grid-template-columns:repeat(var(--pp-cols),minmax(0,1fr))] sm:min-w-0 sm:flex-1 sm:[grid-template-columns:repeat(var(--pp-cols-sm),minmax(0,1fr))]"
          style={{ ['--pp-cols' as string]: cols, ['--pp-cols-sm' as string]: colsSm }}
        >
          {qcm.options.map((o, i) => {
            const st = keyState(qcm, o.id, o.is_correct);
            const isFound = !qcm.revealed && !!found?.includes(o.id);
            const isCrossed = !qcm.revealed && !!crossed?.includes(o.id);
            const selected = st === 'selected' || (qcm.revealed && qcm.chosen.includes(o.id));
            return (
              <button
                key={o.id}
                type="button"
                role={qcm.multiple ? 'checkbox' : 'radio'}
                aria-checked={selected}
                aria-label={`${keyLetter(i)}${letters ? '' : ` : ${o.label.replace(/\$/g, '')}`}${KEY_STATE_LABEL[st]}${isFound ? ', dans ton brouillon' : ''}${isCrossed ? ', fausse d’après ton brouillon' : ''}`}
                className="pp-key"
                data-state={st}
                data-layout={qcm.layout}
                data-multiple={qcm.multiple || undefined}
                disabled={qcm.revealed || qcm.busy || !qcm.shown}
                onClick={() => qcm.toggle(o.id)}
              >
                <span className="pp-key-letter" aria-hidden>
                  {qcm.multiple && st === 'selected' && !letters ? '✓' : keyLetter(i)}
                </span>
                {!letters && (
                  <span className="min-w-0 truncate text-[15px]" aria-hidden>
                    <MarkdownMath>{o.label}</MarkdownMath>
                  </span>
                )}
                {isFound && (
                  <span className="pp-key-dot" data-tone="good" aria-hidden>
                    ✓
                  </span>
                )}
                {isCrossed && (
                  <span className="pp-key-dot" data-tone="bad" aria-hidden>
                    ✗
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {qcm.revealed && onNext && (
          <button
            type="button"
            className="pp-check font-display flex w-full items-center justify-center gap-2 text-base sm:w-auto sm:min-w-[9rem] sm:shrink-0 sm:px-6"
            onClick={onNext}
            data-testid="dock-next"
          >
            {nextLabel}
          </button>
        )}
        {!qcm.revealed && (
          <button
            type="button"
            className="pp-check font-display flex w-full items-center justify-center gap-2 text-base sm:w-auto sm:min-w-[9rem] sm:shrink-0 sm:px-6"
            disabled={qcm.selected.length === 0 || qcm.busy}
            aria-busy={qcm.busy || undefined}
            onClick={qcm.check}
            data-testid="dock-check"
            data-tuto="paper-check"
          >
            {qcm.busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Envoi de ta réponse…
              </>
            ) : (
              <>
                <Check className="h-5 w-5" aria-hidden />
                Check
              </>
            )}
          </button>
        )}
      </div>

      {error && !qcm.revealed && !qcm.busy && (
        <p role="alert" className="text-sm font-semibold text-[var(--sc-bad-ink)]">
          {error}
        </p>
      )}
    </div>
  );
}

/** Publie la hauteur d'un élément fixé en bas dans `--dock-h` (la page garde sa place sous le texte). */
function useDockHeight(ref: RefObject<HTMLDivElement | null>, enabled: boolean) {
  useLayoutEffect(() => {
    if (!enabled) return;
    const el = ref.current;
    const root = document.documentElement;
    const set = () => root.style.setProperty('--dock-h', `${el?.offsetHeight ?? 0}px`);
    set();
    if (!el || typeof ResizeObserver === 'undefined')
      return () => root.style.removeProperty('--dock-h');
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty('--dock-h');
    };
  }, [ref, enabled]);
}

/**
 * Petit bouton « Suivant » fixé en bas à droite, à la place du dock une fois la réponse donnée : la correction du
 * prof est écrite sur la feuille, plus rien à choisir.
 */
export function PaperNextButton({
  label,
  onClick,
  className,
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDockHeight(ref, true);
  return (
    <div
      ref={ref}
      className={cn(
        'pp-root pp-dock-fixed pointer-events-none fixed bottom-0 right-0 z-40 p-4 pb-[max(1rem,env(safe-area-inset-bottom))]',
        className,
      )}
    >
      <button
        type="button"
        className="pp-check font-display pointer-events-auto min-h-11 px-5 text-sm shadow-lg"
        onClick={onClick}
        data-testid="dock-next"
      >
        {label}
      </button>
    </div>
  );
}

/** Le dock fixe du bas. Sa hauteur est publiée dans `--dock-h`. */
export function AnswerDock({
  children,
  label,
  fixed = true,
  className,
}: {
  children: ReactNode;
  label: string;
  /** faux : dock posé dans le flux (carte d'une page, écran d'un téléphone) */
  fixed?: boolean;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useDockHeight(ref, fixed);
  return (
    <div
      ref={ref}
      role="toolbar"
      aria-label={label}
      className={cn(
        'pp-root pp-dock',
        fixed
          ? 'pp-dock-fixed fixed inset-x-0 bottom-0 z-40 pb-[max(0.5rem,env(safe-area-inset-bottom))]'
          : 'relative pb-2',
        className,
      )}
      data-testid="answer-dock"
    >
      <div className="mx-auto max-w-3xl px-3 pt-2">{children}</div>
    </div>
  );
}
